import React, { useState } from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongTwoToneIcon from "@mui/icons-material/ReceiptLongTwoTone";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import CheckTwoToneIcon from "@mui/icons-material/CheckTwoTone";
import dayjs from "dayjs";
import type { IInvoicePayment } from "../type/exhibitorType";

interface PaymentDetailsModalProps {
	open: boolean;
	handleClose: () => void;
	payment?: IInvoicePayment | null;
	paymentDetails?: Record<string, any> | string | null;
}

const keyLabelMap: Record<string, string> = {
	trxID: "Transaction ID",
	trx_id: "Transaction ID",
	paymentID: "Payment ID",
	payment_id: "Payment ID",
	amount: "Amount",
	intent: "Intent",
	currency: "Currency",
	payerType: "Payer Type",
	payer_type: "Payer Type",
	statusCode: "Status Code",
	status_code: "Status Code",
	payerAccount: "Payer Account",
	payer_account: "Payer Account",
	statusMessage: "Status Message",
	status_message: "Status Message",
	customerMsisdn: "Customer MSISDN",
	customer_msisdn: "Customer MSISDN",
	payerReference: "Payer Reference",
	payer_reference: "Payer Reference",
	transactionStatus: "Transaction Status",
	transaction_status: "Transaction Status",
	paymentExecuteTime: "Payment Execute Time",
	payment_execute_time: "Payment Execute Time",
	maxRefundableAmount: "Max Refundable Amount",
	max_refundable_amount: "Max Refundable Amount",
	merchantInvoiceNumber: "Merchant Invoice No",
	merchant_invoice_number: "Merchant Invoice No",
};

const formatKey = (key: string): string => {
	if (keyLabelMap[key]) return keyLabelMap[key];

	return key
		.replace(/([A-Z])/g, " $1")
		.replace(/_/g, " ")
		.replace(/^./, (str) => str.toUpperCase())
		.trim();
};

const isCopyable = (key: string) => {
	const lower = key.toLowerCase();
	return (
		lower.includes("trx") ||
		lower.includes("paymentid") ||
		lower.includes("account") ||
		lower.includes("msisdn") ||
		lower.includes("invoice")
	);
};

const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({
	open,
	handleClose,
	payment,
	paymentDetails,
}) => {
	const theme = useTheme();
	const [copiedKey, setCopiedKey] = useState<string | null>(null);

	const rawData = payment?.payment_details || paymentDetails;

	let details: Record<string, any> = {};
	if (rawData) {
		if (typeof rawData === "string") {
			try {
				details = JSON.parse(rawData);
			} catch {
				details = { raw: rawData };
			}
		} else if (typeof rawData === "object") {
			details = rawData;
		}
	}

	const entries = Object.entries(details).filter(
		([_, val]) => val !== undefined && val !== null && val !== ""
	);

	const handleCopy = (key: string, text: string) => {
		navigator.clipboard.writeText(text);
		setCopiedKey(key);
		setTimeout(() => {
			setCopiedKey(null);
		}, 2000);
	};

	const formatValue = (key: string, value: any): React.ReactNode => {
		if (typeof value === "object") {
			return JSON.stringify(value);
		}

		const str = String(value);

		// Format status with Chip
		if (
			key === "transactionStatus" ||
			key === "transaction_status" ||
			key === "statusMessage" ||
			key === "status_message"
		) {
			const isSuccess =
				str.toLowerCase() === "completed" ||
				str.toLowerCase() === "successful" ||
				str.toLowerCase() === "success";

			return (
				<Chip
					size="small"
					label={str}
					color={isSuccess ? "success" : "default"}
					sx={{ fontWeight: 600, fontSize: "0.75rem", height: 22 }}
				/>
			);
		}

		// Format date if matches execute time or date patterns
		if (
			(key.toLowerCase().includes("time") || key.toLowerCase().includes("date")) &&
			isNaN(Number(str)) &&
			dayjs(str).isValid()
		) {
			return dayjs(str).format("DD-MMM-YYYY hh:mm A");
		}

		// Format amount
		if (
			(key.toLowerCase() === "amount" || key.toLowerCase().includes("refundable")) &&
			!isNaN(Number(str))
		) {
			const curr = details.currency || "BDT";
			return `${curr} ${str}`;
		}

		return str;
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			disableEnforceFocus
			translate="no"
			className="notranslate"
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1.5,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<ReceiptLongTwoToneIcon color="primary" />
					<Typography variant="h4" fontWeight={600}>
						Payment Gateway Details
					</Typography>
				</Box>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ pt: 2, pb: 2 }}>
				{/* Top Summary Banner if payment info is available */}
				{payment && (
					<Paper
						variant="outlined"
						sx={{
							p: 1.5,
							mb: 2,
							borderRadius: 2,
							bgcolor: alpha(theme.palette.primary.main, 0.04),
							borderColor: alpha(theme.palette.primary.main, 0.15),
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: 1.5,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Typography variant="caption" color="text.secondary" fontWeight={600}>
								Payment Type:
							</Typography>
							<Chip
								label={payment.payment_type?.toUpperCase() || "ONLINE"}
								size="small"
								color="primary"
								variant="outlined"
								sx={{ fontWeight: 700 }}
							/>
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Typography variant="caption" color="text.secondary" fontWeight={600}>
								Amount:
							</Typography>
							<Typography variant="subtitle2" fontWeight={700} color="primary.main">
								৳ {payment.amount}
							</Typography>
						</Box>
					</Paper>
				)}

				{/* Key & Value List: colon in middle, key & value aligned touching the colon */}
				{entries.length === 0 ? (
					<Box sx={{ py: 3, textAlign: "center" }}>
						<Typography variant="body2" color="text.secondary">
							No payment details available.
						</Typography>
					</Box>
				) : (
					<Paper
						variant="outlined"
						sx={{
							borderRadius: 2,
							p: 1.5,
							bgcolor: "background.paper",
						}}
					>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
							{entries.map(([key, val], idx) => {
								const formattedVal = formatValue(key, val);
								const canCopy = isCopyable(key) && typeof val !== "object";
								const isCopied = copiedKey === key;

								return (
									<Box
										key={key}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											py: 0.75,
											px: 1,
											borderRadius: 1.2,
											bgcolor:
												idx % 2 === 0
													? alpha(theme.palette.grey[500], 0.04)
													: "transparent",
											transition: "background-color 0.15s",
											"&:hover": {
												bgcolor: alpha(theme.palette.primary.main, 0.05),
											},
										}}
									>
										{/* Key (Right-aligned to touch middle colon) */}
										<Box
											sx={{
												flex: "1 1 48%",
												textAlign: "right",
												pr: 1,
											}}
										>
											<Typography
												variant="body2"
												fontWeight={600}
												color="text.secondary"
												sx={{ fontSize: "0.825rem" }}
											>
												{formatKey(key)}
											</Typography>
										</Box>

										{/* Middle Colon (Perfect vertical alignment) */}
										<Typography
											variant="body2"
											fontWeight={700}
											color="text.secondary"
											sx={{ opacity: 0.7, userSelect: "none" }}
										>
											:
										</Typography>

										{/* Value (Left-aligned to touch middle colon) */}
										<Box
											sx={{
												flex: "1 1 48%",
												textAlign: "left",
												pl: 1,
												display: "flex",
												alignItems: "center",
												gap: 0.6,
												wordBreak: "break-word",
											}}
										>
											{typeof formattedVal === "string" ? (
												<Typography
													variant="body2"
													fontWeight={600}
													color="text.primary"
													sx={{ fontSize: "0.825rem" }}
												>
													{formattedVal}
												</Typography>
											) : (
												formattedVal
											)}

											{canCopy && (
												<Tooltip title={isCopied ? "Copied!" : "Copy"}>
													<IconButton
														size="small"
														onClick={() => handleCopy(key, String(val))}
														sx={{ p: 0.2 }}
													>
														{isCopied ? (
															<CheckTwoToneIcon color="success" sx={{ fontSize: 13 }} />
														) : (
															<ContentCopyTwoToneIcon sx={{ fontSize: 13, opacity: 0.5 }} />
														)}
													</IconButton>
												</Tooltip>
											)}
										</Box>
									</Box>
								);
							})}
						</Box>
					</Paper>
				)}
			</DialogContent>

			<Divider />

			<DialogActions sx={{ p: 1.5, px: 2.5 }}>
				<Button variant="outlined" color="primary" onClick={handleClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PaymentDetailsModal;
